import { useState } from 'react';
import { Alert, Flex, Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
import { creditAdjustmentReasonAsString, ECreditAdjustmentReason } from '@leedpulse-monorepo/enums';

export type CreditsAdjustmentMode = 'add' | 'set' | 'reset';

interface CreditsAdjustmentModalProps {
  mode: CreditsAdjustmentMode;
  currentBalance: number;
  planAllocation: number | null; // leedbucks of the plan, null when unknown
  isBusy: boolean;
  onClose: () => void;
  onSubmit: (value: number, reason: ECreditAdjustmentReason, comment: string | null) => Promise<boolean>;
}

// Staff-facing labels, the client sees creditAdjustmentReasonAsString
const REASON_LABEL: Record<ECreditAdjustmentReason, string> = {
  [ECreditAdjustmentReason.BUG]: 'Bug',
  [ECreditAdjustmentReason.COMMERCIAL_GESTURE]: 'Geste commercial',
  [ECreditAdjustmentReason.CORRECTION]: 'Correction',
  [ECreditAdjustmentReason.OTHER]: 'Autre',
};

const TITLE: Record<CreditsAdjustmentMode, string> = {
  add: 'Ajouter des crédits',
  set: 'Voulez-vous vraiment modifier le solde de crédits ?',
  reset: 'Recréditer cet abonnement selon son plan ?',
};

const OK_TEXT: Record<CreditsAdjustmentMode, string> = {
  add: 'Ajouter',
  set: 'Modifier le solde',
  reset: 'Recréditer',
};

const CreditsAdjustmentModal: React.FC<CreditsAdjustmentModalProps> = ({
  mode,
  currentBalance,
  planAllocation,
  isBusy,
  onClose,
  onSubmit,
}) => {
  const [value, setValue] = useState<number | null>(mode === 'set' ? currentBalance : null);
  const [reason, setReason] = useState<ECreditAdjustmentReason | null>(null);
  const [comment, setComment] = useState('');

  const delta = mode === 'add' ? (value ?? 0) : mode === 'set' ? (value ?? currentBalance) - currentBalance : 0;
  const isValueValid = mode === 'reset' || (mode === 'add' ? delta > 0 : value !== null && delta !== 0);
  const isCommentValid = reason !== ECreditAdjustmentReason.OTHER || comment.trim().length > 0;
  const canSubmit = isValueValid && reason !== null && isCommentValid && !isBusy;

  const submit = async () => {
    if (!canSubmit || reason === null) return;
    const done = await onSubmit(mode === 'reset' ? 0 : (value ?? 0), reason, comment.trim() || null);
    if (done) onClose();
  };

  const confirmAndSubmit = () => {
    // More than a whole month of the plan in one go: ask twice
    if (mode === 'add' && planAllocation !== null && delta > planAllocation) {
      Modal.confirm({
        title: `Ajouter ${delta} crédits ?`,
        content: `C'est plus que l'allocation mensuelle du plan (${planAllocation} crédits).`,
        okText: 'Ajouter',
        cancelText: 'Annuler',
        onOk: submit,
      });
      return;
    }
    submit();
  };

  return (
    <Modal
      title={TITLE[mode]}
      centered
      open
      onCancel={onClose}
      closable={!isBusy}
      maskClosable={!isBusy}
      okText={OK_TEXT[mode]}
      cancelText="Annuler"
      confirmLoading={isBusy}
      okButtonProps={{ danger: mode !== 'add', disabled: !canSubmit }}
      onOk={confirmAndSubmit}
    >
      <Form layout="vertical" style={{ marginTop: '1rem' }}>
        {mode === 'reset' ? (
          <Alert
            type="info"
            style={{ marginBottom: 16 }}
            message="Les crédits sont écrits immédiatement. Le renouvellement sera visible dans l'historique des coûts s'il s'agit d'un plan renouvelable Leedflow."
          />
        ) : (
          <Form.Item
            label={mode === 'add' ? 'Crédits à ajouter' : 'Nouveau solde'}
            extra={`Solde actuel : ${currentBalance} crédits${mode === 'add' && delta > 0 ? ` · nouveau solde : ${currentBalance + delta}` : ''}`}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={mode === 'add' ? 1 : undefined}
              precision={0}
              step={1}
              value={value}
              disabled={isBusy}
              onChange={(v) => setValue(v)}
            />
          </Form.Item>
        )}
        <Form.Item label="Raison" required>
          <Select
            placeholder="Choisir une raison..."
            value={reason}
            disabled={isBusy}
            onChange={(v) => setReason(v)}
            options={Object.values(ECreditAdjustmentReason).map((r) => ({ value: r, label: REASON_LABEL[r] }))}
          />
        </Form.Item>
        <Form.Item
          label="Commentaire"
          required={reason === ECreditAdjustmentReason.OTHER}
          extra="Interne, jamais visible par le client."
        >
          <Input.TextArea
            rows={3}
            value={comment}
            disabled={isBusy}
            onChange={(e) => setComment(e.target.value)}
          />
        </Form.Item>
        {mode !== 'reset' && reason !== null && delta !== 0 && (
          <Flex gap={4}>
            <Typography.Text type="secondary">Visible par le client :</Typography.Text>
            <Typography.Text>{`${creditAdjustmentReasonAsString(reason)} · ${delta > 0 ? '+' : ''}${delta} crédits`}</Typography.Text>
          </Flex>
        )}
      </Form>
    </Modal>
  );
};

export default CreditsAdjustmentModal;